import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { DashboardImageControls } from '../../components/common/DashboardImageControls';
import { DashboardImagePreview } from '../../components/common/DashboardImagePreview';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { useDashboardImageSettings } from '../../hooks/useDashboardImageSettings';
import {
  chainFilterParam,
  enumParam,
  pageParam,
  stringParam,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { ZoomMeta } from '../../hooks/useZoomPreview';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  DesignLockChainSummary,
  DesignLockListEntry,
  DesignLockListResponse,
  DesignLockStatus,
} from '../../types';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';

const PAGE_SIZE = 50;
const LOCKED_DESIGNS_URL_FILTERS = {
  chain: chainFilterParam(),
  status: enumParam(['in_sync', 'diverged'] as const, 'all'),
  q: stringParam(),
  page: pageParam(),
};
const STATUS_TAB_OPTIONS: Array<{ value: DesignLockStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'in_sync', label: 'In sync' },
  { value: 'diverged', label: 'Diverged' },
];

export function formatLockedPercentage(lockCount: number, designCount: number): string {
  if (lockCount === 0 || designCount === 0) return '0%';
  const percentage = (lockCount / designCount) * 100;
  if (percentage >= 10) return `${Math.round(percentage)}%`;
  if (percentage >= 0.1) return `${percentage.toFixed(1)}%`;
  return '<0.1%';
}

function mergeChainSummaries(
  base: DesignLockChainSummary[],
  updates: DesignLockChainSummary[]
): DesignLockChainSummary[] {
  const updatesById = new Map(updates.map((chain) => [chain.chainId, chain]));
  const baseIds = new Set(base.map((chain) => chain.chainId));
  return [
    ...base.map((chain) => updatesById.get(chain.chainId) ?? chain),
    ...updates.filter((chain) => !baseIds.has(chain.chainId)),
  ].sort((left, right) => left.chainId - right.chainId);
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return '-';
  return timestamp.toLocaleString();
}

function divergenceReasons(lock: DesignLockListEntry): string[] {
  return [
    lock.cropsChanged ? 'crops' : null,
    lock.metadataChanged ? 'metadata' : null,
    lock.groupsChanged ? 'groups' : null,
  ].filter((reason): reason is string => Boolean(reason));
}

function designLockZoomMeta(lock: DesignLockListEntry): ZoomMeta {
  return {
    metadataType: 'design',
    id: lock.designId,
    normalizedBarcode: lock.normalizedBarcode,
    cropCount: lock.cropCount,
    updatedAt: lock.updatedAt ?? undefined,
    updatedBy: lock.updatedBy ?? undefined,
  };
}

function visibleRange(page: number, pageSize: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * pageSize + 1;
  return `${start.toLocaleString()}–${Math.min(page * pageSize, total).toLocaleString()}`;
}

interface ChainCardProps {
  chain: DesignLockChainSummary;
  isSelected: boolean;
  isRefreshing: boolean;
  onSelect: () => void;
  onRefresh: () => void;
}

const ChainCard: React.FC<ChainCardProps> = ({
  chain,
  isSelected,
  isRefreshing,
  onSelect,
  onRefresh,
}) => (
  <article className={`dg-chain-card ${isSelected ? 'is-selected' : ''}`}>
    <button type="button" className="dg-chain-select" onClick={onSelect}>
      <span className="dg-chain-title">{chain.chainName}</span>
      <span className="dg-chain-subtitle">chain {chain.chainId}</span>
      <span className="dg-chain-totals">
        <span className="dg-chain-stat">
          <strong>{chain.lockCount.toLocaleString()}</strong>
          locks
        </span>
        <span className="dg-chain-stat">
          <strong>{formatLockedPercentage(chain.lockCount, chain.designCount)}</strong>
          locked
        </span>
      </span>
    </button>
    <div className="dg-chain-bars">
      <span className="dg-chain-bar is-full" title="In sync">
        {chain.inSyncCount.toLocaleString()}
      </span>
      <span className="dg-chain-bar is-partial" title="Diverged">
        {chain.divergedCount.toLocaleString()}
      </span>
      <span className="dg-chain-bar is-muted" title="Unlocked designs">
        {Math.max(0, chain.designCount - chain.lockCount).toLocaleString()}
      </span>
    </div>
    <button
      type="button"
      className="dg-icon-button"
      aria-label={`Refresh ${chain.chainName}`}
      title={`Refresh ${chain.chainName}`}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      <RefreshOutlinedIcon className={isRefreshing ? 'is-spinning' : ''} />
    </button>
  </article>
);

export const LockedDesignsDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [chains, setChains] = useState<DesignLockChainSummary[]>([]);
  const [filters, updateFilters] = useUrlFilterState(LOCKED_DESIGNS_URL_FILTERS);
  const { status: selectedStatus, q: appliedSearch, page } = filters;
  const selectedChainId = filters.chain === 'all' ? null : filters.chain;
  const [queryInput, setQueryInput] = useState(appliedSearch);
  const [response, setResponse] = useState<DesignLockListResponse | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyDesignId, setBusyDesignId] = useState<string | null>(null);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const loadRequestSequence = useRef(0);
  const selectedChainIdRef = useRef(selectedChainId);
  const imageSettings = useDashboardImageSettings('forge-locked-designs-dashboard-crop-size', 42);

  selectedChainIdRef.current = selectedChainId;

  const viewKey = `${selectedChainId ?? ''}|${selectedStatus}|${appliedSearch}|${page}`;
  const viewKeyRef = useRef(viewKey);

  useEffect(() => {
    viewKeyRef.current = viewKey;
  }, [viewKey]);

  useEffect(() => {
    setQueryInput(appliedSearch);
  }, [appliedSearch]);

  const loadSummary = useCallback(async () => {
    setIsLoadingSummary(true);
    try {
      const { chains: chainSummaries } = await ForgeApiService.fetchDesignLockSummary(token);
      const sortedChains = mergeChainSummaries([], chainSummaries);
      setChains(sortedChains);
      const currentChainId = selectedChainIdRef.current;
      if (
        currentChainId === null ||
        !sortedChains.some((chain) => chain.chainId === currentChainId)
      ) {
        const defaultChainId =
          sortedChains.find((chain) => chain.lockCount > 0)?.chainId ??
          sortedChains[0]?.chainId ??
          null;
        updateFilters({ chain: defaultChainId ?? 'all', page: 1 });
      }
      setLoadError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error loading design lock summary';
      setLoadError(message);
      showErrorToast(message);
    } finally {
      setIsLoadingSummary(false);
    }
  }, [showErrorToast, token, updateFilters]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const loadLocks = useCallback(async () => {
    if (selectedChainId === null) {
      setResponse(null);
      setIsLoading(false);
      return;
    }
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const nextResponse = await ForgeApiService.fetchDesignLocks(token, {
        chainId: selectedChainId,
        status: selectedStatus === 'all' ? null : selectedStatus,
        search: appliedSearch || null,
        page,
        pageSize: PAGE_SIZE,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      const responsePageSize = nextResponse.pageSize || PAGE_SIZE;
      const lastPage = Math.max(1, Math.ceil(nextResponse.total / responsePageSize));
      if (
        nextResponse.locks.length === 0 &&
        nextResponse.total > 0 &&
        page > 1 &&
        lastPage < page
      ) {
        updateFilters({ page: lastPage });
        return;
      }
      setResponse(nextResponse);
      setLoadError(null);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading locked designs';
      setResponse(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) setIsLoading(false);
    }
  }, [appliedSearch, page, selectedChainId, selectedStatus, showErrorToast, token, updateFilters]);

  useEffect(() => {
    void loadLocks();
  }, [loadLocks]);

  const handleRefreshChain = async (chainId: number) => {
    const requestViewKey = viewKeyRef.current;
    const refreshesSelectedChain = selectedChainId === chainId;
    const listRequestSequence = refreshesSelectedChain ? loadRequestSequence.current + 1 : null;
    if (listRequestSequence !== null) {
      loadRequestSequence.current = listRequestSequence;
    }
    setRefreshingChainIds((current) => new Set(current).add(chainId));
    try {
      const [summary, refreshedLocks] = await Promise.all([
        ForgeApiService.fetchDesignLockSummary(token),
        refreshesSelectedChain
          ? ForgeApiService.fetchDesignLocks(token, {
              chainId,
              status: selectedStatus === 'all' ? null : selectedStatus,
              search: appliedSearch || null,
              page,
              pageSize: PAGE_SIZE,
            })
          : Promise.resolve(null),
      ]);
      if (viewKeyRef.current !== requestViewKey) return;
      const refreshedChain = summary.chains.find((chain) => chain.chainId === chainId);
      if (refreshedChain) {
        setChains((current) => mergeChainSummaries(current, [refreshedChain]));
      }
      if (
        refreshedLocks &&
        listRequestSequence !== null &&
        loadRequestSequence.current === listRequestSequence
      ) {
        setResponse(refreshedLocks);
        setLoadError(null);
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing locked designs');
    } finally {
      if (listRequestSequence !== null && loadRequestSequence.current === listRequestSequence) {
        setIsLoading(false);
      }
      setRefreshingChainIds((current) => {
        const next = new Set(current);
        next.delete(chainId);
        return next;
      });
    }
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateFilters({ q: queryInput.trim(), page: 1 });
  };

  const handleUpdate = async (lock: DesignLockListEntry) => {
    if (selectedChainId === null || lock.status === 'in_sync') return;
    const reasons = divergenceReasons(lock);
    const summary = reasons.length ? reasons.join(', ') : 'live design content';
    if (
      !window.confirm(
        `Update the lock for ${lock.normalizedBarcode}?\n\nChanged: ${summary}. This replaces the locked snapshot with the current design.`
      )
    ) {
      return;
    }
    setBusyDesignId(lock.designId);
    try {
      await ForgeApiService.updateDesignLock(token, lock.designId, selectedChainId);
      showSuccessToast('Design lock updated');
      await Promise.all([loadSummary(), loadLocks()]);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error updating design lock');
    } finally {
      setBusyDesignId(null);
    }
  };

  const handleUnlock = async (lock: DesignLockListEntry) => {
    if (selectedChainId === null) return;
    if (
      !window.confirm(
        `Unlock ${lock.normalizedBarcode}?\n\nThe saved design snapshot will be permanently removed.`
      )
    ) {
      return;
    }
    setBusyDesignId(lock.designId);
    try {
      await ForgeApiService.deleteDesignLock(token, lock.designId, selectedChainId);
      showSuccessToast('Design unlocked');
      await Promise.all([loadSummary(), loadLocks()]);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error unlocking design');
    } finally {
      setBusyDesignId(null);
    }
  };

  const total = response?.total ?? 0;
  const responsePageSize = response?.pageSize || PAGE_SIZE;
  const hasNext = page * responsePageSize < total;
  const chainOptions = useMemo(() => mergeChainSummaries([], chains), [chains]);

  return (
    <div className="page-content page-content--wide dg-dashboard-page">
      <section className="forge-card dg-dashboard-hero">
        <div className="dg-hero-main">
          <div>
            <div className="dg-dashboard-title">Locked Designs</div>
            <div className="rdc-dashboard-copy">
              Monitor Forge design snapshots and reconcile locks that have diverged from live data.
            </div>
          </div>
          <div className="dg-hero-controls">
            <div className="dg-scope-tabs" role="group" aria-label="Locked designs status filter">
              {STATUS_TAB_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={selectedStatus === option.value ? 'is-active' : ''}
                  onClick={() => updateFilters({ status: option.value, page: 1 })}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <DashboardImageControls
              label="Locked Designs"
              cropSize={imageSettings.cropSize}
              onCropSizeChange={imageSettings.setCropSize}
              zoomEnabled={imageSettings.zoomEnabled}
              onToggleZoom={imageSettings.toggleZoom}
            />
          </div>
        </div>
      </section>

      <section className="dg-chain-grid" aria-label="Locked Designs chain stats">
        {chainOptions.map((chain) => (
          <ChainCard
            key={chain.chainId}
            chain={chain}
            isSelected={selectedChainId === chain.chainId}
            isRefreshing={refreshingChainIds.has(chain.chainId)}
            onSelect={() => {
              updateFilters({ chain: chain.chainId, page: 1 });
            }}
            onRefresh={() => void handleRefreshChain(chain.chainId)}
          />
        ))}
      </section>

      <section className="dg-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Design locks</div>
              <div className="dg-list-count">
                Showing {visibleRange(page, responsePageSize, total)} of {total.toLocaleString()}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={page <= 1 || isLoading}
                onClick={() => updateFilters({ page: Math.max(1, page - 1) })}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {page.toLocaleString()}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!hasNext || isLoading}
                onClick={() => updateFilters({ page: page + 1 })}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
            <form className="dg-list-controls" onSubmit={handleSearch}>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Search locked designs"
                  placeholder="Barcode or item"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                />
              </div>
              {appliedSearch ? (
                <button
                  type="button"
                  className="dg-search-clear"
                  onClick={() => {
                    setQueryInput('');
                    updateFilters({ q: '', page: 1 });
                  }}
                >
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>

          <div className="dg-group-list">
            {isLoading || isLoadingSummary ? (
              <div className="dg-loading-state">Loading Locked Designs...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Locked Designs could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void (selectedChainId === null ? loadSummary() : loadLocks())}
                >
                  Retry
                </button>
              </div>
            ) : response?.locks.length ? (
              <div className="dg-group-table-scroll">
                <table
                  className="dg-group-table locked-designs-table"
                  aria-label="Locked designs table"
                >
                  <thead>
                    <tr>
                      <th>Preview</th>
                      <th>Normalized barcode</th>
                      <th>Status</th>
                      <th>Locked → live crops</th>
                      <th>Locked by / at</th>
                      <th>Updated by / at</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.locks.map((lock) => {
                      const reasons = divergenceReasons(lock);
                      const isBusy = busyDesignId === lock.designId;
                      return (
                        <tr key={lock.designId} className="dg-group-row">
                          <td>
                            <DashboardImagePreview
                              imageUrl={lock.representativeVizUrl}
                              fallback="No image"
                              size={imageSettings.cropSize}
                              alt={`Representative crop for ${lock.normalizedBarcode}`}
                              title={lock.representativeCropId}
                              meta={designLockZoomMeta(lock)}
                              onImageHover={imageSettings.handleImageHover}
                              onImageMove={imageSettings.handleImageMove}
                              onImageLeave={imageSettings.handleImageLeave}
                            />
                          </td>
                          <td className="dg-row-id" title={lock.designId}>
                            {lock.normalizedBarcode}
                          </td>
                          <td>
                            <span
                              className={`dg-scope-pill ${
                                lock.status === 'in_sync' ? 'is-full' : 'is-partial'
                              }`}
                            >
                              {lock.status === 'in_sync' ? 'In sync' : 'Diverged'}
                            </span>
                            {reasons.length ? (
                              <span className="locked-designs-reasons">{reasons.join(', ')}</span>
                            ) : null}
                          </td>
                          <td className="dg-row-count">
                            {lock.lockedActiveCropCount.toLocaleString()} →{' '}
                            {lock.liveActiveCropCount.toLocaleString()}
                          </td>
                          <td className="locked-designs-audit">
                            <strong>{lock.lockedBy}</strong>
                            <span>{formatTimestamp(lock.lockedAt)}</span>
                          </td>
                          <td className="locked-designs-audit">
                            <strong>{lock.updatedBy ?? '-'}</strong>
                            <span>{formatTimestamp(lock.updatedAt)}</span>
                          </td>
                          <td>
                            <div className="locked-designs-actions">
                              <MasterLabelLink
                                className="dg-secondary-button"
                                to={buildMasterLabelUrl({
                                  chainId: selectedChainId as number,
                                  designId: lock.designId,
                                })}
                              >
                                <OpenInNewOutlinedIcon />
                                Open in Master Label
                              </MasterLabelLink>
                              <button
                                type="button"
                                className="dg-secondary-button"
                                disabled={lock.status === 'in_sync' || isBusy}
                                onClick={() => void handleUpdate(lock)}
                              >
                                Update lock
                              </button>
                              <button
                                type="button"
                                className="dg-secondary-button locked-designs-unlock"
                                disabled={isBusy}
                                onClick={() => void handleUnlock(lock)}
                              >
                                Unlock
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No Locked Designs match this view.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
